<template>
  <div id="editor" ref="editor">
  </div>
</template>

<script>
import eventBus from '@/eventbus'
import _ from 'lodash'
import { mapGetters, mapMutations, mapActions } from 'vuex'
import CodeMirror from 'codemirror/lib/codemirror.js'
import 'codemirror/lib/codemirror.css'
import 'codemirror/addon/fold/foldgutter.css'
// import 'codemirror/mode/javascript/javascript.js'
import 'codemirror/mode/markdown/markdown.js'
import 'codemirror/mode/gfm/gfm.js'
import 'codemirror/addon/fold/markdown-fold.js'
// import 'codemirror/addon/fold/foldcode.js'
import 'codemirror/addon/fold/foldgutter.js'
// import 'codemirror/addon/fold/brace-fold.js'
// import 'codemirror/addon/fold/indent-fold.js'
import MarkdownIt from 'markdown-it'
import 'codemirror/addon/display/placeholder.js'
import { operateError } from '@/common/errors'

// https://codemirror.net/addon/fold/foldgutter.js
export default {
  data () {
    return {
      editor: null,
      headers: [],
      syncScrollItems: [],
      codeMirrorOnChangeEffectDebounce: _.debounce(this.codeMirrorOnChangeEffect, 500),
      onScrollEffectDebounce: _.debounce(this.onScrollEffect, 500)
    }
  },
  mounted () {
    this.initCodeMirror()
    this.bindEvents()
  },
  beforeDestroy () {
    this.unbindEvents()
  },
  computed: {
    ...mapGetters('workbench/current', {
      // content: 'content',
      preview: 'preview',
      syncScrollPreviewPosition: 'syncScrollPreviewPosition'
    }),
    ...mapGetters('workbench/outline', {
      foldedHeaderLineNumbers: 'foldedHeaderLineNumbers'
    })
  },
  watch: {
    preview (newValue) {
      if (!newValue) {
        this.$nextTick(() => {
          const that = this
          window.setTimeout(() => {
            that.editor.refresh()
          }, 0)
          // that.editor.refresh()
          that.syncScrollTop()
          that.updateSyncScrollCodePosition()
        })
      }
    }
  },
  methods: {
    ...mapMutations({
      setContent: 'workbench/current/SET_NOTE_CONTENT',
      setSyncScrollItems: 'workbench/current/SET_SYNC_SCROLL_ITEMS',
      setOutlineHeaders: 'workbench/outline/SET_HEADERS',
      setOutlineActiveHeaderLineNumber: 'workbench/outline/SET_ACTIVE_HEADER_LINENUMBER',
      setOutlineFoldedHeaderLineNumbers: 'workbench/outline/SET_FOLDED_HEADER_LINENUMBERS',
      setSyncScrollCodePosition: 'workbench/current/SET_SYNC_SCROLL_CODE_POSITION',
      addOutlineFoldedHeaderLineNumber: 'workbench/outline/ADD_FOLDED_HEADER_LINENUMBER',
      removeOutlineFoldedHeaderLineNumber: 'workbench/outline/REMOVE_FOLDED_HEADER_LINENUMBER',
      setPreview: 'workbench/current/SET_PREVIEW'
    }),
    ...mapMutations('search', {
      setSearchVisible: 'SET_VISIBLE'
    }),
    ...mapActions({
      saveNoteContent: 'workbench/current/changeNoteContent'
    }),
    initCodeMirror () {
      this.editor = CodeMirror(this.$refs.editor, {
        placeholder: '请输入笔记内容',
        mode: 'markdown',
        value: '',
        foldGutter: true,
        gutters: ['CodeMirror-foldgutter'],
        indentUnit: 0,
        tabSize: 4,
        lineWrapping: true,
        cursorScrollMargin: 60 //,
        // lineNumbers: true
      })
      this.editor.setOption('extraKeys', {
        'Ctrl-S': async () => {
          try {
            await this.saveNoteContent()
          } catch (e) {
            console.dir(e)
            operateError(e, '保存笔记')
          }
        },
        'Ctrl-Alt-P': () => {
          this.setPreview(!this.preview)
        },
        'Ctrl-Q': () => {
          this.setSearchVisible(true)
        }
      })
      window.editor = this.editor
    },
    bindEvents () {
      this.bindDomEvents()
      this.bindCodeMirrorEvents()
      this.bindEventBus()
    },
    unbindEvents () {
      this.unbindDomEvents()
      this.unbindCodeMirrorEvents()
      this.unbindEventBus()
    },
    bindDomEvents () {
      const pageEl = window.document.getElementById('pc-page__note')
      pageEl.addEventListener('scroll', this.onScrollHandler)
      pageEl.addEventListener('click', this.codeMirrorFocus)
    },
    unbindDomEvents () {
      const pageEl = window.document.getElementById('pc-page__note')
      if (pageEl) {
        pageEl.removeEventListener('scroll', this.onScrollHandler)
        pageEl.removeEventListener('click', this.codeMirrorFocus)
      }
    },
    bindCodeMirrorEvents () {
      this.editor.on('change', this.codeMirrorOnChangeHandler)
      this.editor.on('fold', this.codeMirrorOnFoldHandler)
      this.editor.on('unfold', this.codeMirrorOnUnfoldHandler)
    },
    unbindCodeMirrorEvents () {
      this.editor.off('change', this.codeMirrorOnChangeHandler)
      this.editor.off('fold', this.codeMirrorOnFoldHandler)
      this.editor.off('unfold', this.codeMirrorOnUnfoldHandler)
    },
    bindEventBus () {
      eventBus.$on('WORKBENCH_CODEMIRROR_EDITOR_TOGGLE_FOLDED', this.toggleFolded)
      eventBus.$on('WORKBENCH_CODEMIRROR_EDITOR_SCROLL_LINE_INTOVIEW', this.scrollLineIntoView)
      eventBus.$on('WORKBENCH_CODEMIRROR_EDITOR_IS_FOLDED', this.isFolded)
      eventBus.$on('WORKBENCH_CODEMIRROR_EDITOR_SET_VALUE', this.setValue)
      eventBus.$on('WORKBENCH_CODEMIRROR_EDITOR_INSERT_TEXT', this.insertText)
    },
    unbindEventBus () {
      eventBus.$off('WORKBENCH_CODEMIRROR_EDITOR_TOGGLE_FOLDED', this.toggleFolded)
      eventBus.$off('WORKBENCH_CODEMIRROR_EDITOR_SCROLL_LINE_INTOVIEW', this.scrollLineIntoView)
      eventBus.$off('WORKBENCH_CODEMIRROR_EDITOR_IS_FOLDED', this.isFolded)
      eventBus.$off('WORKBENCH_CODEMIRROR_EDITOR_SET_VALUE', this.setValue)
      eventBus.$off('WORKBENCH_CODEMIRROR_EDITOR_INSERT_TEXT', this.insertText)
    },
    codeMirrorOnChangeHandler () {
      // 将内容更新提取出变成实时的
      const content = this.editor.getValue()
      this.setContent(content)

      this.codeMirrorOnChangeEffectDebounce()
    },
    codeMirrorOnFoldHandler (editor, pos) {
      const header = _.find(this.headers, n => n.lineNumber === pos.line)
      if (header && !header.empty) {
        this.addOutlineFoldedHeaderLineNumber(pos.line)
      }
    },
    codeMirrorOnUnfoldHandler (editor, pos) {
      const header = _.find(this.headers, n => n.lineNumber === pos.line)
      if (header && !header.empty) {
        this.removeOutlineFoldedHeaderLineNumber(pos.line)
      }
    },
    onScrollHandler () {
      if (!this.preview) {
        this.onScrollEffectDebounce()
      }
    },
    codeMirrorFocus (event) {
      const path = event.path
      const excluded = ['pc-note-editor__name', 'pc-note-editor__tags', 'pc-note__tools']
      if (path.some(n => excluded.indexOf(n.className) > -1)) {
        return
      }
      this.editor.focus()
    },
    // calculateHeaders () {
    //   const headers = []
    //   let lineNumber = 0
    //   this.editor.doc.eachLine(line => {
    //     if ((line.stateAfter.header > 0 || (line.stateAfter.lookAhead === 1 && line.stateAfter.state.header > 0)) && line.text !== '') {
    //       if (!(line.text.indexOf('=') === 0 || line.text.indexOf('--') === 0)) {
    //         const level = line.stateAfter.header || line.stateAfter.state.header
    //         headers.push({
    //           text: line.text.replace(/^#+\s+|\s+#+$/g, ''),
    //           level,
    //           lineNumber,
    //           folded: this.isFolded(lineNumber) !== undefined
    //         })
    //       }
    //     }
    //     lineNumber++
    //   })
    //   return headers
    // },
    // calculateActivityHeaderLineNumber () {
    //   const topVisibleLineNummber = this.editor.lineAtHeight(100, 'window')
    //   let activeHeaderLineNumber = -1
    //   for (let i = 0; i < this.headers.length; i++) {
    //     const header = this.headers[i]
    //     if (header.lineNumber <= topVisibleLineNummber) {
    //       activeHeaderLineNumber = header.lineNumber
    //       continue
    //     }
    //     break
    //   }
    //   if (activeHeaderLineNumber === -1) {
    //     activeHeaderLineNumber = this.headers[0].lineNumber
    //   }
    //   return activeHeaderLineNumber
    // },
    getActiveHeaderLineNumber () {
      // const topVisibleLineNummber = this.editor.lineAtHeight(100, 'window')
      // let activeHeaderLineNumber = -1
      // for (let i = 0; i < this.headers.length; i++) {
      //   const header = this.headers[i]
      //   if (header.lineNumber <= topVisibleLineNummber) {
      //     activeHeaderLineNumber = header.lineNumber
      //     continue
      //   }
      //   break
      // }
      // if (activeHeaderLineNumber === -1 && this.headers.length > 0) {
      //   activeHeaderLineNumber = this.headers[0].lineNumber
      // }
      // return activeHeaderLineNumber
      if (this.headers.length === 0) {
        return -1
      }
      const topVisibleLineNummber = this.editor.lineAtHeight(50, 'window')
      let activeHeaderLineNumber = this.headers[0].lineNumber
      for (let i = 1; i < this.headers.length; i++) {
        const header = this.headers[i]
        if (header.lineNumber <= topVisibleLineNummber) {
          activeHeaderLineNumber = header.lineNumber
        } else {
          break
        }
      }
      return activeHeaderLineNumber
    },
    isFolded (line) {
      var marks = this.editor.findMarks(CodeMirror.Pos(line, 0), CodeMirror.Pos(line + 1, 0))
      for (var i = 0; i < marks.length; ++i) {
        if (marks[i].__isFold) {
          var fromPos = marks[i].find(-1)
          if (fromPos && fromPos.line === line) {
            return marks[i]
          }
        }
      }
    },
    toggleFolded (line) {
      // const header = _.find(this.headers, n => n.lineNumber === line)
      // if (header && !header.empty) {
      //   if (_.indexOf(this.foldedHeaderLineNumbers, line) > -1) {
      //     const folded = this.isFolded(line)
      //     folded.clear()
      //     // this.removeOutlineFoldedHeaderLineNumber(line)
      //   } else {
      //     const state = this.editor.state.foldGutter
      //     const opts = state.options
      //     this.editor.foldCode(CodeMirror.Pos(line, 0), opts)
      //     // this.addOutlineFoldedHeaderLineNumber(line)
      //   }
      //   // this.updateActiveHeaderLineNumber()
      // }
      const folded = this.isFolded(line)
      if (folded) {
        folded.clear()
      } else {
        const state = this.editor.state.foldGutter
        const opts = state.options
        this.editor.foldCode(CodeMirror.Pos(line, 0), opts)
      }
    },
    scrollLineIntoView (line) {
      const heightAtLine = this.editor.heightAtLine(line) // - 50
      window.document.getElementById('pc-page__note').scrollTo(0, heightAtLine + window.document.getElementById('pc-page__note').scrollTop)
    },
    updateActiveHeaderLineNumber () {
      const activeHeaderLineNumber = this.getActiveHeaderLineNumber()
      this.setOutlineActiveHeaderLineNumber(activeHeaderLineNumber)
    },
    codeMirrorOnChangeEffect () {
      // 更新内容
      const content = this.editor.getValue()

      /* 将设置content不再放到debounce（问题:20201027_1022） */
      // this.setContent(content)

      const md = new MarkdownIt({
        html: true
      })
      const allTokens = md.parse(content)

      // 计算出全部标题
      const headers = []
      const foldedHeaderLineNumbers = []
      for (let i = 0; i < allTokens.length;) {
        const token = allTokens[i]
        if (token.type === 'heading_open') {
          const header = {
            level: +(token.tag.replace('h', '')),
            lineNumber: token.map[0],
            tag: token.tag,
            text: allTokens[i + 1].content,
            empty: allTokens[i + 3] === undefined || allTokens[i + 3].tag === token.tag
          }
          headers.push(header)

          if (!header.empty && this.isFolded(header.lineNumber) !== undefined) {
            foldedHeaderLineNumbers.push(header.lineNumber)
          }

          i += 2
        } else {
          i++
        }
      }
      this.setOutlineHeaders(headers)
      this.setOutlineFoldedHeaderLineNumbers(foldedHeaderLineNumbers)
      this.headers = headers

      // 计算出全部展示块
      const items = []
      const filterTokenTypes = ['paragraph_open', 'heading_open', 'list_item_open', 'table_open', 'fence']
      const tokens = allTokens.filter(n => !n.hidden && filterTokenTypes.indexOf(n.type) > -1)
      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i]
        const item = {
          tag: token.tag,
          startLine: token.map[0]
        }
        if (i > 0) {
          const prevItem = items[i - 1]
          prevItem.endLine = item.startLine - 1
          prevItem.lineCount = prevItem.endLine - prevItem.startLine + 1
        }
        items.push(item)
      }
      this.setSyncScrollItems(items)
      this.syncScrollItems = items

      // 更新当前高亮标题
      this.updateActiveHeaderLineNumber()
    },
    onScrollEffect () {
      // 更新当前高亮标题
      this.updateActiveHeaderLineNumber()
      this.updateSyncScrollCodePosition()
    },
    updateSyncScrollCodePosition () {
      const topLineNumber = this.editor.lineAtHeight(0, 'window')
      let item
      for (let i = 0; i < this.syncScrollItems.length; i++) {
        const current = this.syncScrollItems[i]
        if (current.startLine <= topLineNumber && current.endLine >= topLineNumber) {
          item = current
          break
        }
      }

      let syncScrollCodePosition
      if (item) {
        const startLineTop = this.editor.heightAtLine(item.startLine, 'window')
        const endLineBottom = this.editor.heightAtLine(item.endLine + 1, 'window')
        const percent = (-startLineTop) / (-startLineTop + endLineBottom)
        syncScrollCodePosition = {
          item,
          percent
        }
      } else {
        syncScrollCodePosition = {
          item: null,
          percent: 0
        }
      }
      this.setSyncScrollCodePosition(syncScrollCodePosition)
    },
    syncScrollTop () {
      const syncScrollItem = this.syncScrollPreviewPosition.item
      const containerEl = window.document.querySelector('#pc-page__note')
      if (syncScrollItem === null) {
        containerEl.scrollTo(0, 0)
      } else {
        const contailerScrollTop = containerEl.scrollTop
        const startLineTop = this.editor.heightAtLine(syncScrollItem.startLine, 'window')
        const endLineBottom = this.editor.heightAtLine(syncScrollItem.endLine + 1, 'window')
        const height = endLineBottom - startLineTop
        const newScrollTop = height * this.syncScrollPreviewPosition.percent + contailerScrollTop + startLineTop
        containerEl.scrollTo(0, newScrollTop)
      }
    },
    setValue (value) {
      this.editor.setValue(value)
    },
    insertText (text) {
      if (text && text !== '') {
        const doc = this.editor.getDoc()
        const cursor = doc.getCursor()
        const line = doc.getLine(cursor.line)
        const pos = {
          line: cursor.line,
          ch: line.length
        }
        doc.replaceRange(text, pos)
      }
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/variables.scss';
#editor {
  height: auto;
  min-height: 20rem;
  padding-right: 1.4rem;
  margin-bottom: 3rem;
  margin-top: 1rem;

  .CodeMirror {
    height: 100%;
    font-family: $code-font-family;
    font-size: 1.4rem;
    line-height: 2.2rem;

    .CodeMirror-vscrollbar {
      display: none !important;
    }
  }

  .CodeMirror pre.CodeMirror-placeholder { color: #999; }

  .CodeMirror-gutter-elt {
    // line-height: 100%;
  }

  .CodeMirror-foldgutter-open:after {
    font-family: element-icons!important;
    font-size: 1.2rem;
    // font-weight: bold;
    content: "\e6df";
  }

  .CodeMirror-foldgutter-folded:after {
    font-family: element-icons!important;
    font-size: 1.2rem;
    // font-weight: bold;
    content: "\e6e0";
  }

  .CodeMirror-gutters {
    border: none;
    background: transparent;
  }

  .CodeMirror-foldgutter {
    width: 2rem;
  }

  .CodeMirror pre.CodeMirror-line, .CodeMirror pre.CodeMirror-line-like {
    padding: 0;
  }

  #icon::after {
    content: "\e6d9";
  }
}
</style>
